import { createContext, useContext, useState, useEffect } from 'react';
import {
  getMe,
  loginUser,
  logoutUser,
  sendRegisterOTP,
  verifyRegisterOTP,
  googleLogin,
  mergeCartAPI,
  mergeWishlistAPI,
} from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ═══════════════════════════════════════════
     Rehydrate user on mount (page refresh)
  ═══════════════════════════════════════════ */
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }

    getMe()
      .then((res) => {
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      })
      .finally(() => setLoading(false));
  }, []);

  /* ═══════════════════════════════════════════
     Token helpers
  ═══════════════════════════════════════════ */
  const storeTokens = (access, refresh) => {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  };

  const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  };

  /* ═══════════════════════════════════════════
     Merge localStorage cart + wishlist → DB
    Called after login
  ═══════════════════════════════════════════ */
  const mergeLocalDataToDB = async () => {
    // ─── 1. Merge Cart ───
    try {
      const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
      if (Array.isArray(localCart) && localCart.length > 0) {
        const items = localCart.map((i) => ({
          product_id: i.id || i.product_id,
          quantity: i.quantity || 1,
          size: i.size || '',
        }));

        await mergeCartAPI(items);
        localStorage.removeItem('cart');
      }
    } catch (e) {
      console.warn('Cart merge failed:', e);
    }

    // ─── 2. Merge Wishlist ───
    try {
      const guestWishlistRaw = localStorage.getItem('wishlist_guest');
      if (guestWishlistRaw) {
        const ids = JSON.parse(guestWishlistRaw);
        if (Array.isArray(ids) && ids.length > 0) {
          await mergeWishlistAPI(ids);
          localStorage.removeItem('wishlist_guest');
        }
      }
    } catch (e) {
      console.warn('Wishlist merge failed:', e);
    }
  };

  /* ═══════════════════════════════════════════
     Common post-login handler
  ═══════════════════════════════════════════ */
  const handleAuthSuccess = async (data) => {
    storeTokens(data.access, data.refresh);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    await mergeLocalDataToDB();
    return data;
  };

  /* ═══════════════════════════════════════════
     Login (email + password)
  ═══════════════════════════════════════════ */
  const login = async (email, password) => {
    const res = await loginUser({ email, password });
    return handleAuthSuccess(res.data);
  };

  /* ═══════════════════════════════════════════
     Send OTP for registration
  ═══════════════════════════════════════════ */
  const sendOTPForRegistration = async (username, email, password) => {
    const res = await sendRegisterOTP({ username, email, password });
    return res.data;
  };

  /* ═══════════════════════════════════════════
     Verify OTP & create account
  ═══════════════════════════════════════════ */
  const verifyOTPAndRegister = async (email, otp) => {
    const res = await verifyRegisterOTP({ email, otp });
    return handleAuthSuccess(res.data);
  };

  /* ═══════════════════════════════════════════
     Google Login
  ═══════════════════════════════════════════ */
  const loginWithGoogle = async (credential) => {
    // ⚠️ Send 'credential', not 'access_token'
    // (the backend expects the 'credential' key)
    const res = await googleLogin({ access_token: credential });
    return handleAuthSuccess(res.data);
  };

  /* ═══════════════════════════════════════════
     Logout
  ═══════════════════════════════════════════ */
  const logout = async () => {
    try {
      await logoutUser();
    } catch {
      // ignore network errors
    }
    clearTokens();
    setUser(null);
  };

  /* ═══════════════════════════════════════════
     Context value
  ═══════════════════════════════════════════ */
  const value = {
    user,
    loading,
    login,
    sendOTPForRegistration,
    verifyOTPAndRegister,
    loginWithGoogle,
    logout,
    setUser,   // ✅ Exposed for Profile page to update user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);